import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import portalApi from '../api/portalApi';
import { portalAuthService, ClientLoginCredentials } from '../api/portalAuthService';

export interface PortalClient {
  clientId: number;
  clientUserName: string;
  clientEmail: string | null;
}

interface PortalAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  client: PortalClient | null;
  login: (credentials: ClientLoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  refreshClient: () => Promise<void>;
}

const PortalAuthContext = createContext<PortalAuthContextType | undefined>(undefined);

export const usePortalAuth = (): PortalAuthContextType => {
  const ctx = useContext(PortalAuthContext);
  if (ctx === undefined) {
    throw new Error('usePortalAuth must be used within PortalAuthProvider');
  }
  return ctx;
};

export const PortalAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [client, setClient] = useState<PortalClient | null>(null);
  const navigate = useNavigate();

  const fetchClient = useCallback(async (): Promise<void> => {
    try {
      const { data } = await portalApi.get<PortalClient>('/client-view-profile');
      setClient(data);
    } catch {
      setClient(null);
    }
  }, []);

  useEffect(() => {
    const token = portalAuthService.getToken();
    if (!token) {
      setIsAuthenticated(false);
      setClient(null);
      setIsLoading(false);
      return;
    }
    (async () => {
      try {
        await portalAuthService.validateClientSession();
        await fetchClient();
        setIsAuthenticated(true);
      } catch {
        setIsAuthenticated(false);
        setClient(null);
        portalAuthService.clearToken();
      } finally {
        setIsLoading(false);
      }
    })();
  }, [fetchClient]);

  const login = useCallback(
    async (credentials: ClientLoginCredentials) => {
      await portalAuthService.clientLogin(credentials);
      await fetchClient();
      setIsAuthenticated(true);
      navigate('/portal/dashboard');
    },
    [fetchClient, navigate]
  );

  const logout = useCallback(async () => {
    try {
      await portalAuthService.clientLogout();
    } catch {
      // ignore
    } finally {
      setIsAuthenticated(false);
      setClient(null);
      navigate('/portal/login');
    }
  }, [navigate]);

  const refreshClient = useCallback(async () => {
    if (isAuthenticated) await fetchClient();
  }, [isAuthenticated, fetchClient]);

  const value: PortalAuthContextType = {
    isAuthenticated,
    isLoading,
    client,
    login,
    logout,
    refreshClient,
  };

  return (
    <PortalAuthContext.Provider value={value}>
      {children}
    </PortalAuthContext.Provider>
  );
};
